import { Ref, useCallback, useImperativeHandle } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import SelectListItemWithDescription, {
  ListItemWithDescription,
} from "@coworker/apprestructured/src/task-management/components/SelectListItemWithDescription/SelectListItemWithDescription";
import { useTranslation } from "@coworker/locales";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";

export type SelectListItemWithDescriptionCreatorFragmentConfigProps = {
  // Translation key for the title
  title: string;
  field: string;
  items: ListItemWithDescription[];
};

type SelectListItemWithDescriptionCreatorFragmentProps = {
  configProps: SelectListItemWithDescriptionCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

type ListItem = {
  value: string;
  // Translation key for the label
  label: string;
  description: string;
};

function SelectListItemWithDescriptionCreatorFragment({
  configProps,
  commonProps,
  ref,
}: SelectListItemWithDescriptionCreatorFragmentProps) {
  const { field, title, items } = configProps;
  const { blockInput, task, onTaskChange, showValidation, taskType } =
    commonProps;
  const { t } = useTranslation();

  const listItem = validateInitialDetailsFieldValue<ListItem>(
    getByPath(task, field),
    field,
    taskType
  );

  const handleConfirmClick = useCallback(
    (newItem: ListItem) => {
      onTaskChange(
        setByPath(field, {
          value: newItem.value,
          label: newItem.label,
          description: newItem.description,
        })
      );
    },
    [field, onTaskChange]
  );

  const valid = !isLoading(listItem) && listItem !== null;

  useImperativeHandle(ref, () => {
    return { valid };
  });

  if (isLoading(listItem)) {
    return <LoadingCreatorFragment />;
  }

  return (
    <SelectListItemWithDescription
      title={t(title)}
      label={listItem?.label ?? ""}
      description={listItem?.description ?? ""}
      value={listItem?.value ?? ""}
      listItems={items}
      onConfirmClick={handleConfirmClick}
      disabled={blockInput}
      isError={!valid && showValidation}
    />
  );
}

export default SelectListItemWithDescriptionCreatorFragment;
